import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Plus, Minus, Camera, Users, Play, Heart, Meh, ThumbsDown, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';

export interface ModifierOption {
  id: string;
  name: string;
  price?: number;
  nestedModifiers?: ModifierGroup[];
}

export interface ModifierGroup {
  id: string;
  name: string;
  required: boolean;
  multiSelect: boolean;
  minSelect?: number;
  maxSelect?: number;
  maxPerAnswer?: number;
  numFree?: number;
  options: ModifierOption[];
}

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image?: string;
  video?: string;
  category: string;
  popular?: boolean;
  kudozPromotion?: {
    type: 'double' | 'bonus' | 'daily-special';
    amount?: number;
    label?: string;
  };
  modifiers?: ModifierGroup[];
  friendRecommendations?: number;
  friendPhotos?: Array<{ url: string; friendName: string; friendAvatar: string }>;
}

interface ItemReview {
  id: string;
  rating: number;
  text: string | null;
  itemName: string;
  createdAt: string;
  user: {
    id: string;
    displayName: string;
    avatar: string | null;
  };
  overallRating: number;
  media: Array<{
    id: string;
    url: string;
    type: string;
    caption: string | null;
  }>;
}

interface ItemDetailModalProps {
  item: MenuItem;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (item: MenuItem, quantity: number, selections: { [groupId: string]: string[] }, specialInstructions?: string) => void;
  orderingEnabled?: boolean;
  isInOrderMode?: boolean;
  onStartOrder?: () => void;
  restaurantId?: string;
  isEditMode?: boolean;
  initialQuantity?: number;
  initialSelections?: { [groupId: string]: string[] };
  initialSpecialInstructions?: string;
  personalReview?: { rating: number; photoUrl?: string };
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks}w ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears}y ago`;
}

function RatingPill({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'overlay' }) {
  const isOverlay = size === 'overlay';
  const iconClass = isOverlay ? 'w-3 h-3' : 'w-3 h-3';
  const textClass = isOverlay ? 'text-xs font-medium' : 'text-xs';

  if (rating === 3) {
    return (
      <span className={`inline-flex items-center gap-1 ${isOverlay ? 'bg-black/50 backdrop-blur-sm text-white px-2.5 py-1 rounded-md' : ''}`}>
        <Heart className={`${iconClass} ${isOverlay ? 'text-red-400' : 'text-red-500'}`} />
        <span className={textClass}>Loved it</span>
      </span>
    );
  }
  if (rating === 2) {
    return (
      <span className={`inline-flex items-center gap-1 ${isOverlay ? 'bg-black/50 backdrop-blur-sm text-white px-2.5 py-1 rounded-md' : ''}`}>
        <Meh className={`${iconClass} ${isOverlay ? 'text-yellow-400' : 'text-yellow-500'}`} />
        <span className={textClass}>OK</span>
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 ${isOverlay ? 'bg-black/50 backdrop-blur-sm text-white px-2.5 py-1 rounded-md' : ''}`}>
      <ThumbsDown className={`${iconClass} ${isOverlay ? 'text-gray-400' : 'text-muted-foreground'}`} />
      <span className={textClass}>Hated it</span>
    </span>
  );
}

function ItemReviewPhotoStrip({ menuItemId, itemName, restaurantId }: { menuItemId: string; itemName?: string; restaurantId?: string }) {
  const queryParams = new URLSearchParams();
  if (itemName) queryParams.set('itemName', itemName);
  if (restaurantId) queryParams.set('restaurantId', restaurantId);
  const qs = queryParams.toString();
  const { data, isLoading } = useQuery<{ reviews: ItemReview[]; totalReviews: number }>({
    queryKey: ['/api/menu-items', menuItemId, 'reviews', itemName, restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/menu-items/${menuItemId}/reviews${qs ? `?${qs}` : ''}`);
      if (!res.ok) throw new Error('Failed to fetch reviews');
      return res.json();
    },
    enabled: !!menuItemId,
    staleTime: 30000,
  });

  if (isLoading || !data) return null;

  const reviews = data.reviews || [];
  const allMedia = reviews.flatMap(r => r.media);
  if (reviews.length === 0) return null;

  const positiveCount = reviews.filter(r => r.rating === 3).length;
  const neutralCount = reviews.filter(r => r.rating === 2).length;
  const dominantSentiment = positiveCount >= neutralCount && positiveCount > 0 ? 3 : neutralCount > 0 ? 2 : 1;

  return (
    <div className="px-4 pt-3 pb-1 space-y-2">
      <div className="flex items-center gap-2">
        <RatingPill rating={dominantSentiment} />
        <span className="text-xs text-muted-foreground">
          {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
          {allMedia.length > 0 && ` · ${allMedia.length} ${allMedia.length === 1 ? 'photo' : 'photos'}`}
        </span>
      </div>
      {allMedia.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {allMedia.slice(0, 8).map((media) => (
            <div key={media.id} className="relative flex-shrink-0">
              {media.type === 'video' ? (
                <div className="relative w-20 h-20 rounded-md overflow-hidden bg-muted">
                  <video src={media.url} className="w-full h-full object-cover" playsInline muted preload="metadata" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="w-4 h-4 text-white" />
                  </div>
                </div>
              ) : (
                <img
                  src={media.url}
                  alt={media.caption || 'Review photo'}
                  className="w-20 h-20 rounded-md object-cover"
                />
              )}
            </div>
          ))}
          {allMedia.length > 8 && (
            <div className="flex-shrink-0 w-20 h-20 rounded-md bg-muted flex items-center justify-center">
              <span className="text-xs font-medium text-muted-foreground">+{allMedia.length - 8}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ItemReviewsSection({ menuItemId, itemName, restaurantId }: { menuItemId: string; itemName?: string; restaurantId?: string }) {
  const queryParams = new URLSearchParams();
  if (itemName) queryParams.set('itemName', itemName);
  if (restaurantId) queryParams.set('restaurantId', restaurantId);
  const qs = queryParams.toString();
  const { data, isLoading } = useQuery<{ reviews: ItemReview[]; totalReviews: number }>({
    queryKey: ['/api/menu-items', menuItemId, 'reviews', itemName, restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/menu-items/${menuItemId}/reviews${qs ? `?${qs}` : ''}`);
      if (!res.ok) throw new Error('Failed to fetch reviews');
      return res.json();
    },
    enabled: !!menuItemId,
    staleTime: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const reviews = data?.reviews || [];

  if (reviews.length === 0) {
    return (
      <div className="text-center py-6">
        <Camera className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No reviews yet for this item</p>
        <p className="text-xs text-muted-foreground mt-1">Be the first to share your experience!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <span className="text-sm font-medium text-muted-foreground">
        {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
      </span>

      {reviews.map((review) => {
        const hasMedia = review.media.length > 0;
        const primaryMedia = hasMedia ? review.media[0] : null;
        const extraMedia = hasMedia ? review.media.slice(1) : [];

        return (
          <div key={review.id} className="border rounded-lg overflow-hidden">
            {primaryMedia && (
              <div className="relative w-full">
                {primaryMedia.type === 'video' ? (
                  <div className="relative w-full aspect-[4/3] bg-muted">
                    <video
                      src={primaryMedia.url}
                      className="w-full h-full object-cover"
                      playsInline
                      muted
                      preload="metadata"
                      poster={undefined}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
                        <Play className="w-5 h-5 text-white ml-0.5" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={primaryMedia.url}
                    alt={primaryMedia.caption || review.itemName}
                    className="w-full aspect-[4/3] object-cover"
                  />
                )}
                <div className="absolute bottom-2 left-2">
                  <RatingPill rating={review.rating} size="overlay" />
                </div>
                {primaryMedia.caption && (
                  <div className="absolute bottom-2 right-2 max-w-[60%]">
                    <span className="text-xs text-white bg-black/50 backdrop-blur-sm px-2 py-0.5 rounded-md line-clamp-1">
                      {primaryMedia.caption}
                    </span>
                  </div>
                )}
              </div>
            )}

            {extraMedia.length > 0 && (
              <div className="flex gap-1 px-3 pt-2">
                {extraMedia.map((m) => (
                  <div key={m.id} className="relative flex-shrink-0">
                    {m.type === 'video' ? (
                      <div className="relative w-16 h-16 rounded-md overflow-hidden bg-muted">
                        <video src={m.url} className="w-full h-full object-cover" playsInline muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    ) : (
                      <img src={m.url} alt={m.caption || ''} className="w-16 h-16 rounded-md object-cover" />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="px-3 py-2.5 space-y-1.5">
              {!hasMedia && (
                <div>
                  <RatingPill rating={review.rating} />
                </div>
              )}

              {review.text && (
                <p className="text-sm leading-relaxed">{review.text}</p>
              )}

              <div className="flex items-center gap-2">
                <Avatar className="w-5 h-5">
                  <AvatarImage src={review.user.avatar || undefined} alt={review.user.displayName} />
                  <AvatarFallback className="text-[10px]">
                    {review.user.displayName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground truncate">{review.user.displayName}</span>
                <span className="text-xs text-muted-foreground/60">·</span>
                <span className="text-xs text-muted-foreground/60">{formatRelativeTime(review.createdAt)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CollapsibleReviews({ menuItemId, itemName, restaurantId }: { menuItemId: string; itemName?: string; restaurantId?: string }) {
  const [expanded, setExpanded] = useState(false);
  const queryParams = new URLSearchParams();
  if (itemName) queryParams.set('itemName', itemName);
  if (restaurantId) queryParams.set('restaurantId', restaurantId);
  const qs = queryParams.toString();
  const { data } = useQuery<{ reviews: ItemReview[]; totalReviews: number }>({
    queryKey: ['/api/menu-items', menuItemId, 'reviews', itemName, restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/menu-items/${menuItemId}/reviews${qs ? `?${qs}` : ''}`);
      if (!res.ok) throw new Error('Failed to fetch reviews');
      return res.json();
    },
    enabled: !!menuItemId,
    staleTime: 30000,
  });

  const reviews = data?.reviews || [];
  if (reviews.length === 0) return null;

  return (
    <div className="border-t pt-4">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-sm font-semibold">
          See all reviews ({reviews.length})
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="mt-3">
          <ItemReviewsSection menuItemId={menuItemId} itemName={itemName} restaurantId={restaurantId} />
        </div>
      )}
    </div>
  );
}

export default function ItemDetailModal({ item, isOpen, onClose, onAddToCart, orderingEnabled = true, isInOrderMode = true, onStartOrder, restaurantId, isEditMode = false, initialQuantity, initialSelections, initialSpecialInstructions, personalReview }: ItemDetailModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [selections, setSelections] = useState<{ [groupId: string]: string[] }>({});
  const [specialInstructions, setSpecialInstructions] = useState('');

  const initializeSelections = (modifiers: ModifierGroup[] | undefined): { [groupId: string]: string[] } => {
    const result: { [groupId: string]: string[] } = {};
    modifiers?.forEach(group => {
      result[group.id] = [];
      group.options.forEach(option => {
        if (option.nestedModifiers) {
          Object.assign(result, initializeSelections(option.nestedModifiers));
        }
      });
    });
    return result;
  };

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialQuantity !== undefined) {
        setQuantity(initialQuantity);
      } else {
        setQuantity(1);
      }
      if (isEditMode && initialSpecialInstructions !== undefined) {
        setSpecialInstructions(initialSpecialInstructions);
      } else {
        setSpecialInstructions('');
      }
      if (isEditMode && initialSelections !== undefined) {
        // Merge initialSelections with blank structure so all groups are represented
        const blank = initializeSelections(item.modifiers);
        setSelections({ ...blank, ...initialSelections });
      } else {
        setSelections(initializeSelections(item.modifiers));
      }
    }
  }, [isOpen, item.id]);

  if (!isOpen) return null;

  const findModifierGroup = (groupId: string, modifiers: ModifierGroup[] | undefined): ModifierGroup | null => {
    if (!modifiers) return null;
    for (const group of modifiers) {
      if (group.id === groupId) return group;
      for (const option of group.options) {
        if (option.nestedModifiers) {
          const found = findModifierGroup(groupId, option.nestedModifiers);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const handleRadioChange = (groupId: string, optionId: string) => {
    setSelections(prev => {
      const newSelections = { ...prev, [groupId]: [optionId] };
      const group = findModifierGroup(groupId, item.modifiers);
      if (group) {
        const previouslySelected = prev[groupId]?.[0];
        if (previouslySelected && previouslySelected !== optionId) {
          const oldOption = group.options.find(o => o.id === previouslySelected);
          if (oldOption?.nestedModifiers) {
            const clearNested = (mods: ModifierGroup[]) => {
              mods.forEach(m => {
                newSelections[m.id] = [];
                m.options.forEach(o => {
                  if (o.nestedModifiers) clearNested(o.nestedModifiers);
                });
              });
            };
            clearNested(oldOption.nestedModifiers);
          }
        }
      }
      return newSelections;
    });
  };

  const handleCheckboxChange = (groupId: string, optionId: string, checked: boolean) => {
    const group = findModifierGroup(groupId, item.modifiers);
    if (!group) return;

    setSelections(prev => {
      const current = prev[groupId] || [];
      let updated: string[];
      const newSelections = { ...prev };

      if (checked) {
        if (group.maxSelect && current.length >= group.maxSelect) {
          return prev;
        }
        updated = [...current, optionId];
      } else {
        updated = current.filter(id => id !== optionId);
        const option = group.options.find(o => o.id === optionId);
        if (option?.nestedModifiers) {
          const clearNested = (mods: ModifierGroup[]) => {
            mods.forEach(m => {
              newSelections[m.id] = [];
              m.options.forEach(o => {
                if (o.nestedModifiers) clearNested(o.nestedModifiers);
              });
            });
          };
          clearNested(option.nestedModifiers);
        }
      }

      newSelections[groupId] = updated;
      return newSelections;
    });
  };

  const calculateGroupTotal = (group: ModifierGroup): number => {
    const selectedOptionIds = selections[group.id] || [];
    if (selectedOptionIds.length === 0) return 0;

    const prices: number[] = [];
    let nestedTotal = 0;

    for (const optionId of selectedOptionIds) {
      const option = group.options.find(opt => opt.id === optionId);
      if (option) {
        prices.push(option.price || 0);
        if (option.nestedModifiers) {
          for (const ng of option.nestedModifiers) {
            nestedTotal += calculateGroupTotal(ng);
          }
        }
      }
    }

    let groupTotal = 0;
    if (group.numFree && group.numFree > 0) {
      const sorted = [...prices].sort((a, b) => a - b);
      for (let i = 0; i < sorted.length; i++) {
        if (i >= group.numFree) groupTotal += sorted[i];
      }
    } else {
      groupTotal = prices.reduce((sum, p) => sum + p, 0);
    }

    return groupTotal + nestedTotal;
  };

  const calculateNestedTotal = (modifiers: ModifierGroup[] | undefined): number => {
    if (!modifiers) return 0;
    return modifiers.reduce((sum, group) => sum + calculateGroupTotal(group), 0);
  };

  const calculateTotal = () => {
    let total = item.price + calculateNestedTotal(item.modifiers);
    return total * quantity;
  };

  const validateGroup = (group: ModifierGroup): boolean => {
    const selected = selections[group.id] || [];
    const minRequired = group.minSelect ?? (group.required ? 1 : 0);
    if (group.required || minRequired > 0) {
      if (selected.length < minRequired) return false;
    }
    for (const optionId of selected) {
      const option = group.options.find(o => o.id === optionId);
      if (option?.nestedModifiers) {
        for (const nestedGroup of option.nestedModifiers) {
          if (!validateGroup(nestedGroup)) return false;
        }
      }
    }
    return true;
  };

  const canAddToCart = () => {
    if (!item.modifiers) return true;
    return item.modifiers.every(group => validateGroup(group));
  };

  const getFreeOptionIds = (group: ModifierGroup): Set<string> => {
    const freeIds = new Set<string>();
    if (!group.numFree || group.numFree <= 0) return freeIds;
    const selectedOptionIds = selections[group.id] || [];
    if (selectedOptionIds.length === 0) return freeIds;

    const optionsWithPrices = selectedOptionIds
      .map(id => {
        const opt = group.options.find(o => o.id === id);
        return opt ? { id, price: opt.price || 0 } : null;
      })
      .filter(Boolean) as { id: string; price: number }[];

    const sorted = [...optionsWithPrices].sort((a, b) => a.price - b.price);
    for (let i = 0; i < Math.min(group.numFree, sorted.length); i++) {
      freeIds.add(sorted[i].id);
    }
    return freeIds;
  };

  const renderModifierGroup = (group: ModifierGroup, depth: number) => {
    const isNested = depth > 0;
    const freeOptionIds = getFreeOptionIds(group);
    
    return (
      <div 
        key={group.id} 
        className={`space-y-3 ${isNested ? 'ml-4 pl-4 border-l-2 border-primary/20' : ''}`}
      >
        <div>
          <h3 className={`font-semibold ${isNested ? 'text-sm' : ''}`}>{group.name}</h3>
          <p className="text-xs text-muted-foreground">
            {group.required ? 'Required' : 'Optional'}
            {group.multiSelect && group.maxSelect && ` · Choose up to ${group.maxSelect}`}
            {group.numFree && group.numFree > 0 && ` · First ${group.numFree} free`}
          </p>
        </div>

        {group.multiSelect ? (
          <div className="space-y-2">
            {group.options.map((option) => {
              const isSelected = (selections[group.id] || []).includes(option.id);
              return (
                <div key={option.id}>
                  <div
                    className={`flex items-center justify-between p-3 border rounded-lg ${
                      isSelected && option.nestedModifiers ? 'border-primary bg-primary/5' : ''
                    }`}
                    data-testid={`option-${option.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        id={`${group.id}-${option.id}`}
                        checked={isSelected}
                        onCheckedChange={(checked) => 
                          handleCheckboxChange(group.id, option.id, checked as boolean)
                        }
                        data-testid={`checkbox-${option.id}`}
                      />
                      <Label
                        htmlFor={`${group.id}-${option.id}`}
                        className="flex-1 cursor-pointer font-normal"
                      >
                        {option.name}
                        {option.nestedModifiers && option.nestedModifiers.length > 0 && (
                          <span className="text-xs text-muted-foreground ml-1">
                            (has options)
                          </span>
                        )}
                      </Label>
                    </div>
                    {option.price !== undefined && option.price > 0 && (
                      <div className="flex items-center gap-2">
                        {isSelected && freeOptionIds.has(option.id) ? (
                          <span className="text-sm font-medium text-green-600 dark:text-green-400">FREE</span>
                        ) : (
                          <span className="text-sm font-medium">
                            +${option.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {isSelected && option.nestedModifiers && option.nestedModifiers.length > 0 && (
                    <div className="mt-2 space-y-3">
                      {option.nestedModifiers.map(nestedGroup => 
                        renderModifierGroup(nestedGroup, depth + 1)
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <RadioGroup
            value={selections[group.id]?.[0] || ''}
            onValueChange={(value) => handleRadioChange(group.id, value)}
          >
            <div className="space-y-2">
              {group.options.map((option) => {
                const isSelected = (selections[group.id] || []).includes(option.id);
                return (
                  <div key={option.id}>
                    <div
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        isSelected && option.nestedModifiers ? 'border-primary bg-primary/5' : ''
                      }`}
                      data-testid={`option-${option.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <RadioGroupItem
                          value={option.id}
                          id={`${group.id}-${option.id}`}
                          data-testid={`radio-${option.id}`}
                        />
                        <Label
                          htmlFor={`${group.id}-${option.id}`}
                          className="flex-1 cursor-pointer font-normal"
                        >
                          {option.name}
                          {option.nestedModifiers && option.nestedModifiers.length > 0 && (
                            <span className="text-xs text-muted-foreground ml-1">
                              (has options)
                            </span>
                          )}
                        </Label>
                      </div>
                      {option.price !== undefined && option.price > 0 && (
                        isSelected && freeOptionIds.has(option.id) ? (
                          <span className="text-sm font-medium text-green-600 dark:text-green-400">FREE</span>
                        ) : (
                          <span className="text-sm font-medium">
                            +${option.price.toFixed(2)}
                          </span>
                        )
                      )}
                    </div>
                    {isSelected && option.nestedModifiers && option.nestedModifiers.length > 0 && (
                      <div className="mt-2 space-y-3">
                        {option.nestedModifiers.map(nestedGroup => 
                          renderModifierGroup(nestedGroup, depth + 1)
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </RadioGroup>
        )}
      </div>
    );
  };

  return (
    /* Outer overlay: full-screen on mobile, dark backdrop + centred on md+ */
    <div
      className="fixed inset-0 z-[60] bg-background md:bg-black/60 md:flex md:items-center md:justify-center"
      data-testid={`modal-item-${item.id}`}
    >
      {/* Inner modal: full-screen on mobile, constrained dialog on md+ */}
      <div className="relative bg-background w-full h-full flex flex-col md:max-w-2xl md:h-auto md:max-h-[90vh] md:rounded-xl md:overflow-hidden">
      {/* Header */}
      <div className="shrink-0 sticky top-0 z-10 bg-background border-b">
        <div className="flex items-center justify-between p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="button-close-modal"
          >
            <X className="w-5 h-5" />
          </Button>
          <h2 className="font-semibold truncate flex-1 text-center">{item.name}</h2>
          <div className="w-10" />
        </div>
      </div>

      {/* Content — scrollable, takes remaining height via flex-1 */}
      <div className="flex-1 overflow-y-auto">
        <div className="pb-4">
          {/* Personal review photo — shown above item image when user has a photo of this dish */}
          {personalReview?.photoUrl && (
            <div className="relative w-full bg-muted">
              <img
                src={personalReview.photoUrl}
                alt="Your photo"
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-3 left-3 flex items-center gap-2">
                {personalReview.rating === 3 ? (
                  <Heart className="w-4 h-4 text-red-400" />
                ) : personalReview.rating === 2 ? (
                  <Meh className="w-4 h-4 text-yellow-400" />
                ) : (
                  <ThumbsDown className="w-4 h-4 text-gray-300" />
                )}
                <span className="text-sm font-medium text-white">Your photo</span>
              </div>
            </div>
          )}

          {/* Video or Image */}
          {item.video ? (
            <div className="w-full bg-muted relative">
              <video
                src={item.video}
                controls
                playsInline
                muted
                preload="metadata"
                className="w-full h-64 object-cover"
                data-testid="video-item"
                poster={item.image}
              />
            </div>
          ) : item.image && (
            <div className="w-full bg-muted">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-64 object-cover"
                data-testid="image-item"
              />
            </div>
          )}

          {/* Compact review photo strip for Tier 2 — above item name/price, only when ordering */}
          {orderingEnabled && isInOrderMode && (
            <ItemReviewPhotoStrip menuItemId={item.id} itemName={item.name} restaurantId={restaurantId} />
          )}

          <div className="p-4 space-y-6">
            {/* Item Info */}
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h2 className="text-xl font-bold">{item.name}</h2>
                {item.price > 0 && (
                  <span className="text-lg font-semibold text-muted-foreground">${item.price.toFixed(2)}</span>
                )}
                {item.popular && (
                  <Badge variant="secondary" className="text-xs" data-testid="badge-popular">
                    Popular
                  </Badge>
                )}
                {item.kudozPromotion && (
                  <Badge variant="secondary" className="text-xs" data-testid="badge-kudoz-promo">
                    {item.kudozPromotion.type === 'double' && '2x'}
                    {item.kudozPromotion.type === 'bonus' && item.kudozPromotion.amount && `+${item.kudozPromotion.amount}`}
                    {item.kudozPromotion.type === 'daily-special' && item.kudozPromotion.amount && `+${item.kudozPromotion.amount}`}
                  </Badge>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              )}
              
              {item.friendRecommendations && item.friendRecommendations > 0 && (
                <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span className="font-medium" data-testid="friend-recommendations">
                    {item.friendRecommendations} {item.friendRecommendations === 1 ? 'friend recommends' : 'friends recommend'} this item
                  </span>
                </div>
              )}
            </div>

            {/* Friend Photos */}
            {item.friendPhotos && item.friendPhotos.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    Photos from friends ({item.friendPhotos.length})
                  </span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {item.friendPhotos.map((photo, index) => (
                    <div key={index} className="relative flex-shrink-0">
                      <img
                        src={photo.url}
                        alt={`Photo by ${photo.friendName}`}
                        className="w-24 h-24 rounded-lg object-cover"
                        data-testid={`image-friend-photo-${index}`}
                      />
                      <Avatar className="absolute -bottom-1 -right-1 w-6 h-6 border-2 border-background">
                        <AvatarImage src={photo.friendAvatar} alt={photo.friendName} />
                        <AvatarFallback>{photo.friendName.charAt(0)}</AvatarFallback>
                      </Avatar>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ordering-specific content: Modifiers + Special Instructions — only in order mode */}
            {orderingEnabled && isInOrderMode && (
              <>
                {item.modifiers?.map((group) => renderModifierGroup(group, 0))}

                <div className="space-y-2">
                  <Label htmlFor="special-instructions" className="text-sm font-semibold">
                    Special instructions or requests
                  </Label>
                  <Textarea
                    id="special-instructions"
                    placeholder="E.g., extra napkins, no onions, etc."
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    className="min-h-20 resize-none"
                    data-testid="input-special-instructions"
                  />
                </div>

                {/* Collapsible full reviews section */}
                <CollapsibleReviews menuItemId={item.id} itemName={item.name} restaurantId={restaurantId} />
              </>
            )}

            {/* Reviews/media: shown for Tier 1 always, and for Tier 2 in browse mode */}
            {(!orderingEnabled || !isInOrderMode) && (
              <ItemReviewsSection menuItemId={item.id} itemName={item.name} restaurantId={restaurantId} />
            )}
          </div>
        </div>
      </div>{/* end scrollable content */}

      {/* Footer - ordering mode: quantity controls + Add to Order */}
      {orderingEnabled && isInOrderMode && (
        <div className="shrink-0 bg-background border-t p-4 space-y-3">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              data-testid="button-decrease-quantity"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="font-medium w-8 text-center" data-testid="text-quantity">
              {quantity}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(quantity + 1)}
              data-testid="button-increase-quantity"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <Button
            className="w-full"
            size="lg"
            onClick={() => {
              onAddToCart(item, quantity, selections, specialInstructions.trim() || undefined);
              onClose();
            }}
            disabled={!canAddToCart()}
            data-testid="button-add-to-order"
          >
            {isEditMode ? `Update Item · $${calculateTotal().toFixed(2)}` : `Add To Order · $${calculateTotal().toFixed(2)}`}
          </Button>
        </div>
      )}

      {/* Footer - browse mode for Tier 2: prompt user to set up order context */}
      {orderingEnabled && !isInOrderMode && (
        <div className="shrink-0 bg-background border-t p-4">
          <Button
            className="w-full"
            size="lg"
            onClick={() => {
              onClose();
              onStartOrder?.();
            }}
            data-testid="button-start-order"
          >
            Set Up Your Order to Add Items
          </Button>
        </div>
      )}
      </div>{/* end inner modal */}
    </div>
  );
}
